import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, LinkSimple, Trash, Storefront, Sparkle, CheckCircle, Trophy } from "@phosphor-icons/react";

/**
 * <ComparePrices kind="purchase"|"wishlist" id={...} source={{retailer_name, current_price, linked_listings}} onChange={refresh} />
 */
export default function ComparePrices({ kind, id, source, onChange }) {
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState(null);
  const [linking, setLinking] = useState(null); // url being linked

  const linked = source.linked_listings || [];
  const allListings = [
    { retailer_id: source.retailer_id, retailer_name: source.retailer_name, current_price: source.current_price, url: source.product_url, primary: true },
    ...linked,
  ];
  const cheapest = allListings.reduce((min, l) => (l.current_price < (min?.current_price ?? Infinity) ? l : min), null);

  const findMatches = async () => {
    setSearching(true);
    setMatches(null);
    try {
      const { data } = await api.post(`/${kind === "wishlist" ? "wishlist" : "purchases"}/${id}/find-matches`);
      setMatches(data.matches || []);
      if ((data.matches || []).length === 0) toast.info("No high-confidence matches found at other retailers.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Match search failed");
    } finally { setSearching(false); }
  };

  const link = async (m) => {
    setLinking(m.url);
    try {
      await api.post(`/${kind === "wishlist" ? "wishlist" : "purchases"}/${id}/link-listing`, { url: m.url });
      toast.success(`Linked ${m.retailer_name}`);
      setMatches((prev) => (prev || []).filter((x) => x.url !== m.url));
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    } finally { setLinking(null); }
  };

  const unlink = async (retailer_id) => {
    try {
      await api.delete(`/${kind === "wishlist" ? "wishlist" : "purchases"}/${id}/link-listing/${retailer_id}`);
      toast.success("Unlinked");
      onChange?.();
    } catch (e) { toast.error("Unlink failed"); }
  };

  const isPurchase = kind === "purchase";
  return (
    <div className="border border-[#e4e4e7] bg-white p-6" data-testid="compare-prices-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pg-overline">{isPurchase ? "Price match opportunities" : "Compare across retailers"}</div>
          <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">{isPurchase ? "Is it cheaper somewhere else?" : "Cross-retailer pricing"}</h2>
          <p className="text-sm text-[#52525b] mt-2 max-w-md">
            {isPurchase
              ? "Most big NZ retailers will price-match a competitor selling the same product for less, inside their price-protection window. If we spot a lower price, we'll surface it so you can ask politely."
              : "Auto-match this product on the other supported NZ retailers. Confirm each suggestion before we start tracking it."}
          </p>
        </div>
        <button onClick={findMatches} disabled={searching} className="bg-[#002FA7] text-white font-bold px-5 py-2.5 inline-flex items-center gap-2 hover:bg-[#00207A] disabled:opacity-60" data-testid="find-matches-btn">
          {searching ? <><Sparkle size={16} className="animate-pulse" /> Searching…</> : <><MagnifyingGlass size={16} weight="bold" /> Find matches</>}
        </button>
      </div>

      {/* Current price grid */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
        {["pb_tech", "jb_hifi", "harvey_norman"].map((rid) => {
          const r = allListings.find((x) => x.retailer_id === rid);
          const name = { pb_tech: "PB Tech", jb_hifi: "JB Hi-Fi", harvey_norman: "Harvey Norman" }[rid];
          const isWinner = r && cheapest && r.retailer_id === cheapest.retailer_id && allListings.length > 1;
          return (
            <div key={rid} className={`bg-white p-4 ${r ? "" : "opacity-50"}`} data-testid={`compare-cell-${rid}`}>
              <div className="flex items-center gap-1.5">
                <Storefront size={12} className="text-[#52525b]" />
                <span className="pg-overline">{name}</span>
                {isWinner && (
                  <span className="ml-1 inline-flex items-center gap-1 bg-[#16A34A] text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5">
                    <Trophy size={9} weight="fill" /> Cheapest
                  </span>
                )}
              </div>
              {r ? (
                <>
                  <div className={`font-heading text-2xl font-black tracking-tighter mt-2 ${isWinner ? "text-[#16A34A]" : ""}`}>${r.current_price.toFixed(2)}</div>
                  <div className="mt-2 flex items-center gap-2">
                    {r.primary ? (
                      <span className="text-[10px] font-bold text-[#002FA7] uppercase tracking-widest">Primary</span>
                    ) : (
                      <>
                        <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs underline text-[#52525b] hover:text-black">View →</a>
                        <button onClick={() => unlink(r.retailer_id)} className="text-xs text-[#52525b] hover:text-[#FF3B30]" title="Unlink" data-testid={`unlink-${rid}`}>
                          <Trash size={12} />
                        </button>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="font-mono-pg text-xs text-[#52525b] mt-3">Not linked yet</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Match suggestions */}
      {matches !== null && (
        <div className="mt-6">
          <div className="pg-overline mb-3">Suggested matches</div>
          {matches.length === 0 ? (
            <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-6 text-center text-sm text-[#52525b]" data-testid="no-matches-found">
              No reliable matches found. JB Hi-Fi auto-match works best when the source product has a brand + model number. For PB Tech / Harvey Norman, GPT-5.2 has to guess URLs which often miss - paste a URL manually below.
            </div>
          ) : (
            <ul className="space-y-3">
              {matches.map((m) => (
                <li key={m.url} className="border border-[#e4e4e7] bg-[#f7f7f8] p-4 flex items-center gap-4" data-testid={`match-suggestion-${m.retailer_id}`}>
                  {m.image && <img src={m.image} alt="" referrerPolicy="no-referrer" onError={(e)=>{ e.currentTarget.style.display="none"; }} className="w-12 h-12 object-cover border border-[#e4e4e7]" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="pg-overline">{m.retailer_name}</span>
                      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: m.confidence >= 0.8 ? "#16A34A" : m.confidence >= 0.5 ? "#002FA7" : "#FF3B30" }}>
                        {Math.round(m.confidence * 100)}% match · {m.method}
                      </span>
                    </div>
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-sm hover:underline truncate block mt-1">{m.name}</a>
                    <div className="font-mono-pg text-xs text-[#52525b] mt-1">${Number(m.price).toFixed(2)} NZD · {m.in_stock ? "In stock" : "Out of stock"}</div>
                  </div>
                  <button onClick={() => link(m)} disabled={linking === m.url} className="border border-black font-bold px-4 py-2 text-sm hover:bg-black hover:text-white disabled:opacity-50 inline-flex items-center gap-1.5" data-testid={`link-match-${m.retailer_id}`}>
                    {linking === m.url ? "Linking…" : <><LinkSimple size={14} weight="bold" /> Link</>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Manual paste fallback */}
      <ManualLink kind={kind} id={id} onLinked={onChange} sourceRetailer={source.retailer_id} />
    </div>
  );
}

function ManualLink({ kind, id, onLinked, sourceRetailer }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!url) return;
    setBusy(true);
    try {
      await api.post(`/${kind === "wishlist" ? "wishlist" : "purchases"}/${id}/link-listing`, { url });
      toast.success("Linked");
      setUrl("");
      onLinked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
    finally { setBusy(false); }
  };
  return (
    <details className="mt-6 border-t border-[#e4e4e7] pt-5">
      <summary className="cursor-pointer text-sm font-semibold flex items-center gap-2" data-testid="manual-link-toggle">
        <LinkSimple size={14} /> Link a URL manually (PB Tech / Harvey Norman / JB Hi-Fi)
      </summary>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input type="url" value={url} onChange={(e)=>setUrl(e.target.value)} placeholder="https://www.pbtech.co.nz/product/..." className="flex-1 border border-[#e4e4e7] py-2 px-3 outline-none focus:border-[#002FA7] text-sm" data-testid="manual-link-input" />
        <button type="submit" disabled={busy || !url} className="bg-[#002FA7] text-white font-bold px-4 py-2 text-sm hover:bg-[#00207A] disabled:opacity-60" data-testid="manual-link-submit">
          {busy ? "Linking…" : "Link"}
        </button>
      </form>
      <p className="text-xs text-[#52525b] mt-2 font-mono-pg">Paste any product URL from a supported retailer. We'll start tracking it daily.</p>
    </details>
  );
}
